import type { ReactNode } from 'react';

import { RiFullscreenExitLine, RiFullscreenLine } from '@remixicon/react';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { ButtonIcon } from '@spotify-internal/encore-web';
import {
  Tooltip as EncoreTooltip,
  TooltipRoot,
} from '@spotify-internal/encore-web/next';

import { FullPlaybackControls } from './FullPlaybackControls';
import { SimplifiedPlaybackControls } from './SimplifiedPlaybackControls';
import type { FullscreenControl } from './types';
import type { PlaybackDefaults } from './usePlaybackState';

type PlaybackControlsVariant = 'full' | 'simplified';

type PlaybackControlsProps = {
  player: CsrPlayerElement;
  metricEvents?: MetricEvent[];
  variant?: PlaybackControlsVariant;
  initialPageUrl?: string;
  playbackDefaults?: PlaybackDefaults;
  onEventMarkerClick?: (timestampSeconds: number) => void;
  fullscreenControl?: FullscreenControl;
  menuContainer?: HTMLElement;
};

const FullscreenButton = ({
  fullscreenControl,
}: {
  fullscreenControl: FullscreenControl;
}): ReactNode => {
  const label = fullscreenControl.isFullscreen
    ? 'Exit fullscreen'
    : 'Enter fullscreen';

  return (
    <TooltipRoot>
      <ButtonIcon
        size="small"
        variant="tertiary"
        aria-label={label}
        data-track-id="button.fullscreen"
        data-track-action="update"
        data-track-data-enabled={!fullscreenControl.isFullscreen}
        onClick={fullscreenControl.onToggle}
      >
        {fullscreenControl.isFullscreen ? (
          <RiFullscreenExitLine size={18} aria-hidden />
        ) : (
          <RiFullscreenLine size={18} aria-hidden />
        )}
      </ButtonIcon>
      <EncoreTooltip>{label}</EncoreTooltip>
    </TooltipRoot>
  );
};

export const PlaybackControls = ({
  player,
  metricEvents = [],
  variant = 'full',
  initialPageUrl,
  playbackDefaults,
  onEventMarkerClick,
  fullscreenControl,
  menuContainer,
}: PlaybackControlsProps) => {
  const fullscreenButton = fullscreenControl ? (
    <FullscreenButton fullscreenControl={fullscreenControl} />
  ) : null;

  if (variant === 'simplified') {
    return (
      <SimplifiedPlaybackControls
        player={player}
        metricEvents={metricEvents}
        playbackDefaults={playbackDefaults}
        fullscreenButton={fullscreenButton}
        menuContainer={menuContainer}
      />
    );
  }

  return (
    <FullPlaybackControls
      player={player}
      metricEvents={metricEvents}
      initialPageUrl={initialPageUrl}
      playbackDefaults={playbackDefaults}
      onEventMarkerClick={onEventMarkerClick}
      fullscreenButton={fullscreenButton}
      menuContainer={menuContainer}
    />
  );
};
